/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

/**
 * HTML5 Video Player Adapter
 *
 * Wraps native HTML5 <video> element with consistent API for unified Player class.
 * All methods return Promises for consistency with other video providers.
 *
 * @typedef {HTMLVideoElement} VideoElement
 */
export class HtmlPlayer {
  /**
   * @param {VideoElement} player - Native HTML5 video element
   * @param {Object} options - Player options
   */
  constructor(player, options = {}) {
    this.player = player;
    this.options = options;
    this.eventListeners = new Map();
  }

  /**
   * Add event listener
   *
   * @param {string} event - Event name
   * @param {Function} listener - Event listener
   * @returns {void}
   */
  on(event, listener) {
    // Map common video event names to HTML5 video events
    const eventMap = {
      play: 'play',
      playing: 'playing',
      pause: 'pause',
      ended: 'ended',
      timeupdate: 'timeupdate',
      progress: 'progress',
      seeking: 'seeking',
      seeked: 'seeked',
      volumechange: 'volumechange',
      ratechange: 'ratechange',
      loadstart: 'loadstart',
      loadeddata: 'loadeddata',
      loadedmetadata: 'loadedmetadata',
      canplay: 'canplay',
      canplaythrough: 'canplaythrough',
      error: 'error',
      stalled: 'stalled',
      waiting: 'waiting',
    };

    const nativeEvent = eventMap[event] || event;
    const wrappedListener = (e) => listener(e);

    // Store the wrapped listener for removal later
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }

    this.eventListeners.get(event).push({listener, wrappedListener});

    this.player.addEventListener(nativeEvent, wrappedListener);
  }

  /**
   * Remove event listener
   *
   * @param {string} event - Event name
   * @param {Function} [listener] - Event listener
   * @returns {void}
   */
  off(event, listener) {
    const eventMap = {
      play: 'play',
      playing: 'playing',
      pause: 'pause',
      ended: 'ended',
      timeupdate: 'timeupdate',
      progress: 'progress',
      seeking: 'seeking',
      seeked: 'seeked',
      volumechange: 'volumechange',
      ratechange: 'ratechange',
      loadstart: 'loadstart',
      loadeddata: 'loadeddata',
      loadedmetadata: 'loadedmetadata',
      canplay: 'canplay',
      canplaythrough: 'canplaythrough',
      error: 'error',
      stalled: 'stalled',
      waiting: 'waiting',
    };

    const nativeEvent = eventMap[event] || event;
    const listeners = this.eventListeners.get(event);

    if (!listeners) {
      return;
    }

    if (listener) {
      // Remove specific listener
      const index = listeners.findIndex((l) => l.listener === listener);

      if (index !== -1) {
        const {wrappedListener} = listeners[index];

        this.player.removeEventListener(nativeEvent, wrappedListener);
        listeners.splice(index, 1);
      }
    } else {
      // Remove all listeners for this event
      listeners.forEach(({wrappedListener}) => {
        this.player.removeEventListener(nativeEvent, wrappedListener);
      });
      this.eventListeners.delete(event);
    }
  }

  /**
   * Load new video by URL
   *
   * @param {string} url - Video URL
   * @returns {Promise<void>}
   */
  async loadVideo(url) {
    this.player.src = url;

    return this.player.load();
  }

  /**
   * Check if playing
   *
   * @returns {Promise<boolean>}
   */
  async isPlaying() {
    return Promise.resolve(
      !this.player.paused && !this.player.ended && this.player.readyState > 2
    );
  }

  /**
   * Check if paused
   *
   * @returns {Promise<boolean>}
   */
  async isPaused() {
    return Promise.resolve(this.player.paused);
  }

  /**
   * Check if ended
   *
   * @returns {Promise<boolean>}
   */
  async isEnded() {
    return Promise.resolve(this.player.ended);
  }

  /**
   * Set autopause (not applicable for HTML5 video)
   *
   * @param {boolean} autopause - Enable autopause
   * @returns {Promise<void>}
   */
  async setAutopause(autopause) {
    return Promise.resolve(null);
  }

  /**
   * Set color (not applicable for HTML5 video)
   *
   * @param {string} color - Hex or rgb color
   * @returns {Promise<void>}
   */
  async setColor(color) {
    return Promise.resolve(null);
  }

  /**
   * Set loop
   *
   * @param {boolean} loop - Enable loop
   * @returns {Promise<void>}
   */
  async setLoop(loop) {
    this.player.loop = loop;

    return Promise.resolve();
  }

  /**
   * Check if muted
   *
   * @returns {Promise<boolean>}
   */
  async isMuted() {
    return Promise.resolve(this.player.muted);
  }

  /**
   * Set muted state
   *
   * @param {boolean} muted - Mute or unmute
   * @returns {Promise<void>}
   */
  async setMuted(muted) {
    this.player.muted = muted;

    return Promise.resolve();
  }

  /**
   * Set playback rate
   *
   * @param {number} rate - Playback rate (0.25, 0.5, 1, 1.5, 2)
   * @returns {Promise<void>}
   */
  async setPlaybackRate(rate) {
    this.player.playbackRate = rate > 10 ? rate / 100 : rate;

    return Promise.resolve();
  }

  /**
   * Set volume
   *
   * @param {number} level - Volume level (0 to 1)
   * @returns {Promise<void>}
   */
  async setVolume(level) {
    // HTML5 video expects 0-1
    this.player.volume = level > 1 ? level / 100 : level;

    return Promise.resolve();
  }

  /**
   * Unload player
   *
   * @returns {Promise<void>}
   */
  async unload() {
    this.player.pause();
    this.player.removeAttribute('src');
    this.player.load();

    return Promise.resolve();
  }

  /**
   * Destroy player
   *
   * @returns {Promise<void>}
   */
  async destroy() {
    // Remove all event listeners
    this.eventListeners.forEach((listeners, event) => {
      this.off(event);
    });
    this.eventListeners.clear();

    // Remove from DOM
    if (this.player.parentNode) {
      this.player.parentNode.removeChild(this.player);
    }

    return Promise.resolve();
  }

  /**
   * Mute video
   *
   * @returns {Promise<void>}
   */
  async mute() {
    this.player.muted = true;

    return Promise.resolve();
  }

  /**
   * Unmute video
   *
   * @returns {Promise<void>}
   */
  async unMute() {
    this.player.muted = false;

    return Promise.resolve();
  }

  /**
   * Play video
   *
   * @returns {Promise<void>}
   */
  async play() {
    return this.player.play();
  }

  /**
   * Pause video
   *
   * @returns {Promise<void>}
   */
  async pause() {
    this.player.pause();

    return Promise.resolve();
  }

  /**
   * Stop video
   *
   * @returns {Promise<void>}
   */
  async stop() {
    this.player.pause();
    this.player.currentTime = 0;

    return Promise.resolve();
  }

  /**
   * Call method directly on player
   *
   * @param {string} method - Method name
   * @param {...any} args - Method arguments
   * @returns {Promise<any>}
   */
  async call(method, ...args) {
    // Map common methods to HTML5 video properties/methods
    const methodMap = {
      getCurrentTime: () => this.player.currentTime,
      setCurrentTime: (time) => {
        this.player.currentTime = time;
      },
      getDuration: () => this.player.duration,
      getVolume: () => this.player.volume,
    };

    if (methodMap[method]) {
      const result = methodMap[method](...args);

      return Promise.resolve(result);
    }

    // Try direct method call
    if (typeof this.player[method] === 'function') {
      const result = this.player[method](...args);

      return result instanceof Promise ? result : Promise.resolve(result);
    }

    // Try property access
    if (args.length === 0) {
      return Promise.resolve(this.player[method]);
    } else {
      this.player[method] = args[0];

      return Promise.resolve();
    }
  }
}
